import React, { useState } from 'react';
import { ChevronLeft, User, Calendar, Save, Trash2 } from 'lucide-react';
import { ChildProfile, useChildProfile } from '../contexts/ChildProfileContext';
import { useTranslation } from '../contexts/TranslationContext';
import { Capacitor } from '@capacitor/core';

interface ChildProfileEditorProps {
  child?: ChildProfile; // undefined = create mode
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
}

const ChildProfileEditor: React.FC<ChildProfileEditorProps> = ({
  child,
  onSave,
  onCancel,
  onDelete,
}) => {
  const { createChild, updateChild, deleteChild } = useChildProfile();
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const isEditMode = !!child;

  const [name, setName] = useState(child?.name || '');
  const [birthDate, setBirthDate] = useState(child?.birthDate || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError(t("profile.le_prenom_est_requis", "First name is required"));
      return;
    }

    if (name.trim().length < 2) {
      setError(t("profile.le_prenom_doit_contenir_au_moins_2_caracteres", "First name must be at least 2 characters"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditMode && child) {
        await updateChild(child.id, {
          name: name.trim(),
          birthDate: birthDate || null,
        });
      } else {
        await createChild(name.trim(), birthDate || undefined);
      }
      onSave();
    } catch (err: any) {
      console.error('Error saving child profile:', err);
      setError(err.message || t("profile.erreur_lors_de_la_sauvegarde", "Error while saving"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!child) return;

    setLoading(true);
    setError(null);

    try {
      await deleteChild(child.id);
      onDelete?.();
    } catch (err: any) {
      console.error('Error deleting child profile:', err);
      setError(err.message || t("profile.erreur_lors_de_la_suppression", "Error while deleting"));
      setLoading(false);
    }
  };

  // Calculate age from birth date
  const calculateAge = (date: string): number => {
    const birth = new Date(date);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className={`h-screen-adaptive bg-gradient-to-b from-purple-100 to-pink-100 ${isNative ? 'safe-area-top safe-area-bottom' : ''}`}>
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={onCancel}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">
            {isEditMode ? t("profile.edit_profile", "Edit profile") : t("profile.nouveau_joueur", "Nouveau joueur")}
          </h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Avatar preview */}
        <div className="flex justify-center mb-6">
          <div className="w-28 h-28 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center shadow-lg">
            {name ? (
              <span className="text-4xl font-bold text-white">
                {name.charAt(0).toUpperCase()}
              </span>
            ) : (
              <User size={40} className="text-white/60" />
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Name field */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User size={16} className="inline mr-2" />
              {t("profile.childs_first_name", "Child's first name")}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("profile.ex_emma", "Ex: Emma")}
              maxLength={50}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
              disabled={loading}
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-2">
              {t("profile.ce_prenom_sera_utilise_pour_personnaliser_les_hist", "This name will be used to personalize stories")}
            </p>
          </div>

          {/* Birth date field */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar size={16} className="inline mr-2" />
              {t("profile.date_de_naissance_optionnel", "Date of birth (optional)")}
            </label>
            <input
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              min={new Date(new Date().setFullYear(new Date().getFullYear() - 12)).toISOString().split('T')[0]}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={loading}
            />
            {birthDate && (
              <p className="text-sm text-purple-600 mt-2">
                {calculateAge(birthDate)} {t("profile.years", "years")}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              {t("profile.allows_adapting_content_to_the_childs_ag", "Allows adapting content to the child's age")}
            </p>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading || !name.trim()}
            className="w-full bg-purple-600 text-white py-4 rounded-xl font-medium text-lg shadow-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                {t("profile.enregistrement", "Saving...")}
              </>
            ) : (
              <>
                <Save size={20} />
                {isEditMode ? t("profile.enregistrer", "Save") : t("profile.creer_le_profil", "Create profile")}
              </>
            )}
          </button>

          {/* Delete button (edit mode only) */}
          {isEditMode && (
            <div className="pt-4 border-t border-gray-200">
              {!showDeleteConfirm ? (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full text-red-600 py-3 rounded-lg font-medium hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 size={18} />
                  {t("profile.supprimer_ce_profil", "Supprimer ce profil")}
                </button>
              ) : (
                <div className="bg-red-50 rounded-lg p-4 space-y-3">
                  <p className="text-red-700 text-sm text-center">
                    {t("profile.delete_the_profile_of", "Delete the profile of")} <strong>{child?.name}</strong> ?
                    <br />
                    <span className="text-xs">{t("profile.all_progress_will_be_lost", "All progress will be lost.")}</span>
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-white"
                      disabled={loading}
                    >
                      {t("profile.cancel", "Cancel")}
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      disabled={loading}
                    >
                      {loading ? t("profile.suppression", "Suppression...") : t("profile.delete", "Delete")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ChildProfileEditor;
